import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { BrandedSpinner } from '@/components/BrandedSpinner'
import ArticleCard from '@/components/ArticleCard'
import { getSavedArticles, saveArticle, unsaveArticle } from '@/hooks/useArticle'

export const Route = createFileRoute('/_auth/saved')({
  component: SavedArticlesPage,
})

function SavedArticlesPage() {
  const { data: savedArticles, isLoading, isError } = getSavedArticles()
  const saveMutation = saveArticle()
  const unsaveMutation = unsaveArticle()
  const [activeToggleId, setActiveToggleId] = useState<string | null>(null)

  const savedIds = useMemo(() => new Set((savedArticles || []).map(article => article.id)), [savedArticles])

  const handleToggleSave = async (articleId: string) => {
    if (!articleId) return
    setActiveToggleId(articleId)
    try {
      if (savedIds.has(articleId)) {
        await unsaveMutation.mutateAsync(articleId)
      } else {
        await saveMutation.mutateAsync(articleId)
      }
    } finally {
      setActiveToggleId(null)
    }
  }

  const getArticleLink = (slug: string | null | undefined, fallbackId: string) => ({
    to: '/articles/$slug',
    params: { slug: slug || fallbackId },
  })

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-primary">
        <BrandedSpinner size="xl" />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-primary">
        <div className="text-xl text-red-500">Error loading saved articles.</div>
      </div>
    )
  }

  if (!savedArticles || savedArticles.length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-primary px-4 text-center">
        <h1 className="text-4xl font-bold text-secondary mb-4">Saved articles</h1>
        <p className="text-tertiary max-w-xl mb-6">
          You haven&apos;t saved any articles yet. Browse the library and tap &quot;Save&quot; to keep favorites here.
        </p>
        <a
          href="/articles"
          className="inline-flex items-center rounded-md bg-violet-600 px-5 py-3 text-white font-semibold shadow hover:bg-violet-500 transition"
        >
          Explore articles
        </a>
      </div>
    )
  }

  return (
    <div className="max-w-[1100px] mx-auto min-h-screen bg-primary">
      <div className="w-full bg-primary px-4 pt-10 pb-6 max-w-7xl mx-auto">
        <h1 className="text-4xl sm:text-5xl font-extrabold text-secondary mb-4">Saved articles</h1>
        <p className="text-tertiary text-base sm:text-lg max-w-2xl">
          All the guides you&apos;ve saved in one place. Tap any card to continue reading or remove it from your list.
        </p>
      </div>
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-5 md:grid-cols-2 gap-8">
          {(savedArticles || []).map(article => (
            <ArticleCard
              key={article.id}
              title={article.title}
              description={article.description || ''}
              thumbnail={article.thumbnail}
              banner={article.banner}
              link={getArticleLink(article.slug ?? undefined, article.id)}
              tags={(article.tags || []).map(tag => ({
                ...tag,
                archived: (tag as any).archived ?? false,
                createdAt: (tag as any).createdAt ?? '',
                updatedAt: (tag as any).updatedAt ?? ''
              }))}
              readTime={article.readTime || undefined}
              updatedAt={article.updatedAt || article.publishedAt || undefined}
              isSaved
              onToggleSave={() => handleToggleSave(article.id)}
              toggleLoading={activeToggleId === article.id || saveMutation.isPending || unsaveMutation.isPending}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
